import { useState } from "react";
import { useTranslation } from "react-i18next";
import { IconWarning } from "@boligportal/bricks-icons";
import {
  createFileRoute,
  type ErrorComponentProps,
  redirect,
} from "@tanstack/react-router";
import { AuthExpiredError } from "~/api/auth/AuthErrors";
import { AuthForgotPasswordCard } from "~/components/auth/components/login/AuthForgotPasswordCard";
import {
  AuthLoginForm,
  type OnLoginSuccessCallback,
} from "~/components/auth/components/login/AuthLoginForm";
import { AuthEmailConfirmationNoticeForm } from "~/components/auth/components/signup/AuthEmailConfirmationNoticeForm";
import { AlertState } from "~/components/bricks/AlertState";
import { Card } from "~/components/ui/Card";
import { useAuth } from "~/hooks/useAuth";
import { returnUrlSearchSchema } from "~/routes/auth/-schemas/return-url-schema";
import { getReturnPath } from "~/routes/auth/-utils/url";

type ErrorState = "token-timed-out" | "unknown-error";

export const Route = createFileRoute("/auth/verify-email/$token")({
  validateSearch: returnUrlSearchSchema,
  beforeLoad: ({ search }) => ({
    returnPath: getReturnPath(search.returnUrl) ?? "/",
  }),
  loader: async ({ params, context: { returnPath, authClient } }) => {
    await authClient.verifyEmail(params.token);

    throw redirect({
      to: returnPath,
      replace: true,
      state: {
        emailVerified: true,
      },
    });
  },
  errorComponent: ErrorComponent,
});

function ErrorComponent(props: ErrorComponentProps) {
  const { returnUrl } = Route.useSearch();

  const { user, authClient } = useAuth();

  const { t } = useTranslation(["auth"]);

  const [localView, setLocalView] = useState<"login" | "forgot-pass">("login");

  const onLoginSuccess: OnLoginSuccessCallback = async () => {
    await authClient.requestEmailVerificationEmail(returnUrl);
  };

  const errorState: ErrorState =
    props.error instanceof AuthExpiredError
      ? "token-timed-out"
      : "unknown-error";

  if (localView === "forgot-pass") {
    return (
      <AuthForgotPasswordCard
        setView={({ view }) => {
          if (view === "login") setLocalView("login");
        }}
      />
    );
  }

  const renderErrorState = () => {
    switch (errorState) {
      case "token-timed-out":
        return user?.requiredActions?.emailVerification ? (
          <AuthEmailConfirmationNoticeForm />
        ) : (
          <AuthLoginForm
            formValues={{ username: "", password: "" }}
            warningText={{
              title: t(
                "auth:email_verification.resend_form.information.heading",
              ),
              content: t(
                "auth:email_verification.resend_form.information.text",
              ),
            }}
            onLoginSuccess={onLoginSuccess}
            onResetPasswordClick={() => setLocalView("forgot-pass")}
          />
        );
      case "unknown-error":
        return (
          <div className="flex justify-center">
            <AlertState
              headline={t(
                "auth:email_verification.resend_form.errors.unknown_error_state.heading",
              )}
              description={t(
                "auth:email_verification.resend_form.errors.unknown_error_state.description",
              )}
              icon={IconWarning}
            />
          </div>
        );
    }
  };

  return (
    <Card className="w-full max-w-109">
      <Card.Content>{renderErrorState()}</Card.Content>
    </Card>
  );
}
